import type { reservation as Fr } from '../fr/reservation';

const jours = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** « reservation » in English — typed on the French: a missing sentence does not compile. */
export const reservation: typeof Fr = {
  datesSignale: 'Date change reported — a manager will validate it in “Approvals”.',
  signalementEchec: 'The report did not go through.',
  cautionTraitee: 'Deposit processed — the deposit refund receipt is ready to print.',
  restitutionEchec: 'The refund did not go through.',
  contratGenere: 'Rental agreement generated — you can print it and record the deposit.',
  cautionEnregistree: (n: string) => `Deposit recorded — receipt ${n} ready to print.`,
  annulee: 'Rental cancelled — the reason is in the audit log.',
  annulationImpossible: 'Could not cancel',
  prolongee: (j: number, m: string) => `Rental extended by ${jours(j)} — new total ${m}.`,
  prolongationImpossible: 'Could not extend',
  resilie: (saisie: boolean) => `Rental agreement terminated${saisie ? ' — deposit forfeited (article 07)' : ''}. The reason is in the audit log.`,
  resiliationImpossible: 'Could not terminate',
  demandeEnvoyee: 'Request sent — the managers have been notified.',
  genererContrat: 'Generate the agreement',
  imprimerContrat: 'Print the agreement',
  enregistrerCaution: 'Record the deposit',
  signalerDates: 'Report a date change',
  restituerCaution: (m: string) => `Refund the deposit · ${m}`,
  encaisserSolde: (m: string) => `Take payment of the balance · ${m}`,
  faireDepart: 'Do the check-out',
  faireRetour: 'Do the check-in',
  corrigerRetour: 'Correct the return (time, mileage, fuel)',
  prolongerLocation: 'Extend the rental',
  annulerLocation: 'Cancel the rental',
  resilierContrat: 'Terminate the agreement',
  cautionNonEnregistree: 'The deposit was not recorded',
  cautionNonEnregistreeTexte: 'The rental has been created and numbered — do not enter it again. Only the deposit remains: issue the rental agreement, then record the deposit. The agreed amount is already filled in.',
  listeNoire: 'Customer on the blacklist',
  derogation: (d: string) => `Override of the eligibility rule — ${d}`,
  derogationDetail: (motif: string, date: string) => `Reason: ${motif} · granted on ${date}`,
  documents: 'Documents to print',
  contratLocation: 'Rental agreement',
  contratDetail: (n: string) => `no. ${n} · bilingual, with the inspection`,
  imprimer: 'Print',
  dechargeRemise: 'Deposit receipt',
  dechargeRemiseDetail: (n: string, m: string) => `no. ${n} · ${m} received`,
  dechargeRestitution: 'Deposit refund receipt',
  numero: (n: string) => `no. ${n} · `,
  facture: (type: string) => (type === 'avoir' ? 'Credit note' : type === 'acompte' ? 'Down-payment invoice' : 'Final invoice'),
  factureDetail: (n: string, d: string, m: string) => `no. ${n} · ${d} · ${m}`,
  dechargeApparaitra: 'The receipt will appear here once the deposit is recorded, and the invoices once they are issued.',
  location: 'Rental',
  prixMois: 'Monthly price',
  nMois: (n: number | undefined) => `${n} ${n === 1 ? 'month' : 'months'} `,
  nJours: (n: number) => `(${jours(n)})`,
  tarifReference: 'Reference rate',
  parJour: (m: string) => `${m} / day`,
  prixNegocie: 'Negotiated price',
  joursSaison: (n: number, saison: string) => `${jours(n)} · ${saison} season`,
  saisons: { haute: 'high', basse: 'low' } as Record<string, string>,
  kilometrage: 'Mileage',
  illimite: 'unlimited',
  forfaitJour: (n: number | undefined, u: string) => `${n} ${u} / day`,
  paiement: 'Payment',
  differe: 'pay later — on account',
  parTranches: 'in instalments',
  surPlace: 'on site',
  totalHt: 'Total excl. tax',
  tva: (pct: number | undefined) => `VAT ${pct}%`,
  totalPrevu: (ttc: boolean) => `Expected total${ttc ? ' incl. tax' : ''}`,
  contratCaution: 'Rental agreement and deposit',
  aucunContrat: 'No rental agreement generated yet. It will be created at check-out.',
  numeroLabel: 'Number',
  edlDepart: 'Check-out inspection',
  edlRetour: 'Check-in inspection',
  encaisse: 'Collected',
  soldeDu: 'Balance due',
  caution: 'Deposit',
  supplementsRetour: 'Extra charges invoiced at check-in',
  colSupplement: 'Extra charge', colCalcul: 'Calculation', colMontant: 'Amount',
  modifieManuellement: 'edited manually',
  etatDesLieux: 'Inspection',
  depart: 'Check-out',
  retour: 'Check-in',
  pasEncore: 'Not recorded yet.',
  date: 'Date',
  carburant: 'Fuel',
  proprete: 'Cleanliness',
  propreteValeur: (pi: number, pe: number) => `${pi}/10 int. · ${pe}/10 ext.`,
  dommages: 'Damage',
  dontNouveaux: (n: number) => ` (incl. ${n} new)`,
  photos: 'Photos',
  ouvrirGrand: 'Open full size',
  saisieDefinitive: 'Your entry is final',
  saisieDefinitiveTexte: 'You cannot edit this file. If something is wrong, request a correction: a manager handles it, and both values stay in the audit log.',
  demanderCorrection: 'Request a correction',
  voirPlanning: 'View on the planning board',
  actuellement: (a: string, b: string) => `Currently from ${a} to ${b}`,
  rienModifie: 'You are not changing anything: the request goes to “Approvals”. As soon as a manager approves it, the dates change and the agreement total follows.',
  nouveauDepart: 'New check-out',
  nouveauRetour: 'New return',
  laissezVide: 'Leave empty if the date does not change.',
  cePasse: 'What happened (required)',
  cePassePlaceholder: 'The customer is returning the car two days later, informed by phone.',
  annuler: 'Cancel',
  envoyerAValider: 'Send to “Approvals”',
  restituerTitre: (n: string) => `Refund deposit ${n}`,
  detenus: (m: string) => `${m} held`,
  appartientClient: 'This money belongs to the customer. Anything you deduct must be justified in writing.',
  montantRetenu: 'Amount deducted',
  montantRetenuAide: 'Leave 0 to refund the full amount.',
  motifRetenue: 'Reason for the deduction (required)',
  motifRetenuePlaceholder: 'Damage found at check-in, missing fuel…',
  aRestituer: 'To refund',
  sigClient: 'Customer signature',
  sigDirecteur: 'Manager signature',
  restituerEditer: 'Refund and issue the receipt',
  cautionPasFixe: 'The deposit is not a fixed amount: you decide it for this rental. A numbered, signed receipt will be generated immediately.',
  montant: 'Amount',
  devise: 'Currency',
  forme: 'Form',
  formes: { especes: 'Cash', cheque: 'Cheque', traite: 'Bill of exchange', carte: 'Bank card' },
  numeroPiece: 'Document no.',
  numeroPieceAide: 'Cheque or bill number.',
  enregistrerEditer: 'Record and issue the receipt',
  retourPrevuA: 'Return currently scheduled: ',
  retourPrevuB: (j: number) => ` — ${jours(j)},`,
  moisRecalcule: 'This rental is monthly: the total is recalculated in whole months.',
  doitEtreApres: 'Must be after the current return. To shorten it, ask a manager.',
  apercuMois: 'the total is recalculated in whole months',
  apercuJours: (j: number, m: string) => `+${jours(j)} · about ${m} in total`,
  motif: 'Reason',
  motifAide: 'Optional, but it stays in the audit log.',
  motifProlongationPlaceholder: 'The customer is keeping the car until Saturday',
  doitEtreLibre: 'The vehicle must be available on the added days',
  dejaPromise: 'If it is already promised to someone else, the software refuses and tells you the date it becomes available.',
  revenir: 'Back',
  prolonger: 'Extend',
  annulerTitre: 'Cancel this rental',
  motifDefinitif: 'The reason is required and final',
  resteJournal: 'It will stay in the audit log with your name. The rental cannot be restored.',
  dejaCommence: 'This rental has already started',
  voitureSortie: 'The vehicle has left: the check-out inspection is recorded. ',
  dejaEncaisses: (m: string) => `${m} has already been collected — cancelling does not refund it or take it out of the cash register. `,
  plutotResilierA: 'If the customer breached their obligations, use ',
  plutotResilierB: '“Terminate the agreement”',
  plutotResilierC: ' instead: article 07 then covers the deposit.',
  motifAnnulation: 'Reason for cancellation',
  motifAnnulationPlaceholder: 'Customer withdrew, vehicle off the road…',
  resiliationManquement: 'Termination for breach',
  article07: 'Your article 07 provides for forfeiting the deposit in case of prohibited use — carrying passengers for hire, driving by a third party, goods. The reason will stay in the audit log.',
  motifResiliation: 'Reason for termination',
  motifResiliationPlaceholder: 'Vehicle used to carry passengers for hire (article 07)',
  saisirCaution: 'Forfeit the deposit under article 07',
  resilier: 'Terminate',
  queCorriger: 'What needs correcting?',
  queCorrigerPlaceholder: 'check-out mileage, return date…',
  valeurEnregistree: 'Recorded value',
  valeurEnregistreeAide: 'What is written today.',
  valeurCorrecte: 'Correct value',
  valeurCorrecteAide: 'What should be there instead.',
  pourquoi: 'Why?',
  pourquoiPlaceholder: 'Digits swapped when entering',
  correctionFinanciere: 'Financial correction',
  toucheArgent: 'It involves money: only the owner can decide on it.',
  envoyerDemande: 'Send the request',
};
